// pages/mobile/WarningPage.js
// Warning availability (mockup 3b) and stand-down messages.
import React, { useCallback } from 'react';
import { useParams } from 'react-router-dom';
import AnswerBar from './AnswerBar';
import { DetailTopBar, LoadState, StatusPill } from './MobileChrome';
import { WarningIcon } from './icons';
import { formatSent } from './format';
import { fetchDetail, useMobileData } from './mobileApi';
import styles from './mobile.module.css';
import useMessageRead from './useMessageRead';

const WARNING_LABELS = {
  question: 'Are you available?',
  confirm: "I'm available",
  deny: 'Not available',
  denyTitle: 'Not available?',
  denySubmit: "Tell dispatch I'm not available",
  confirmedToast: "Sent: you're available.",
  deniedToast: "Sent: you're not available.",
  confirmedState: "You told dispatch you're available.",
  deniedState: "You told dispatch you're not available.",
};

const STAND_DOWN_LABELS = {
  confirm: 'Got it',
  deny: null,
  confirmedToast: 'Thanks. Dispatch knows you saw it.',
  confirmedState: 'You acknowledged this stand down.',
  deniedState: 'You acknowledged this stand down.',
  footnote: 'No answer needed. This just lets dispatch know you saw it.',
};

const WarningPage = () => {
  const { id } = useParams();
  const load = useCallback(() => fetchDetail('warning', id), [id]);
  const { data: warning, error, loading, reload } = useMobileData(load);
  useMessageRead(warning);

  if (!warning) {
    return (
      <main className={styles.page}>
        <DetailTopBar title="Message" />
        <LoadState loading={loading} error={error} onRetry={reload} />
      </main>
    );
  }

  const isStandDown = warning.kind === 'stand_down';
  const acknowledged = (warning.status || '').toLowerCase() === 'confirmed';

  return (
    <main className={`${styles.page} ${styles.pageWithActions}`}>
      <DetailTopBar title={isStandDown ? 'Stand down' : 'Message'} />
      <div className={styles.content}>
        <section className={`${styles.hero} ${styles.heroWarning}`}>
          <div className={styles.heroTop}>
            <span className={styles.statusPill} style={{ paddingLeft: 0 }}>
              <WarningIcon size={16} />
              {isStandDown ? 'STAND DOWN' : 'MESSAGE'}
            </span>
            {!isStandDown && <StatusPill status={warning.status} needsAnswer={warning.needs_answer} />}
          </div>
          <div>
            <div className={styles.heroLabel}>Hi {warning.staff_name},</div>
            <h2 className={styles.heroTitle}>
              {isStandDown ? "Storm passed. You're released." : 'Message'}
            </h2>
          </div>
        </section>

        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>Message from dispatch</h2>
          <div className={styles.card}>
            <p className={styles.bodyText}>{warning.message || 'No message provided.'}</p>
            <p className={styles.mutedText}>{formatSent(warning.sent_at)}</p>
          </div>
        </section>
      </div>

      {isStandDown ? (
        <AnswerBar
          kind="stand_down"
          id={warning.id}
          status={warning.status}
          needsAnswer={!acknowledged}
          canAnswer={warning.can_answer}
          closedReason={warning.closed_reason}
          labels={STAND_DOWN_LABELS}
          allowChange={false}
          onChanged={reload}
        />
      ) : (
        <AnswerBar
          kind="warning"
          id={warning.id}
          status={warning.status}
          needsAnswer={warning.needs_answer}
          canAnswer={warning.can_answer}
          closedReason={warning.closed_reason}
          labels={WARNING_LABELS}
          onChanged={reload}
        />
      )}
    </main>
  );
};

export default WarningPage;
